import { useMutation, useQuery } from "@apollo/client/react";
import { useUser } from "@auth0/nextjs-auth0/client";
import { ZonedDateTime } from "@sa-nu/shared-utils/date";
import type { PriceKey } from "@sa-nu/shared-utils/price";
import {
  formatCurrency,
  priceAmounts,
  toUnitCharacter,
} from "@sa-nu/shared-utils/price";
import clsx from "clsx";
import { useRouter } from "next/navigation";
import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { Controller, useWatch, useFormContext } from "react-hook-form";
import { z } from "zod";

import { useRefetchReservations } from "../../(default)/layout";
import type { DocumentType, FragmentType } from "../../../__generated__/gql";
import { graphql, useFragment } from "../../../__generated__/gql";
import type {
  AccommodationReservationDetailInput,
  AccommodationReservationProvider,
  StripeCard,
} from "../../../__generated__/gql/graphql";
import { AccommodationFacilityType } from "../../../__generated__/gql/graphql";
import { useGtm } from "../../../lib/google-tag-manager/gtm";
// Half Time Projectで一時的に利用したいのでgoogle-tag-manager packageからimportするが、
// google-tag-managerというよりも本来はanalyticsなどといった捉え方が適切なのかもしれない。
// Half Time Project終了後に削除するのと、ユーザーの行動ログ基盤については別途検討する。
import {
  buildAccommodationReservationProviderInput,
  shoudShowRemoteCancelPolicy,
} from "../../../utils";
import { useAlertDialog } from "../../_contexts/alert-dialog";
import { useSnackbar } from "../../_contexts/snackbar";
import { useRuleRevision2027ForStay } from "../../_hooks/transition/use-rule-revision-2027";
import { useAccommodationTermsLink } from "../../_hooks/use-accommodation-terms-link";
import { useInNativeApp } from "../../_hooks/use-in-native-app";
import { getAccommodationFacilitiesLabel } from "../../utils/accommodation-facility";
import { groupFeeItemByNight } from "../../utils/group-fee-item-by-night";
import { postMessageToNativeApp } from "../../utils/native-app";
import { ReservationNoticesSection } from "../accommodation/accommodation-notices";
import AskToSendUrlDialog from "../reservation/ask-to-send-url-dialog";
import CancelReservationDialog from "../reservation/cancel-reservation-dialog";
import { resolveFacilityFeeWaiver } from "../reservation/description/_utils/facility-fee-waiver";
import type { DescriptionForAccommodationClassMessages } from "../reservation/description/description-for-accommodation-class";
import { DescriptionForAccommodationClass } from "../reservation/description/description-for-accommodation-class";
import DescriptionForDogFriendlyFormPartial, {
  dogFriendlyOptionsSchema,
} from "../reservation/description/description-for-dog-friendly-form-partial";
import type { DescriptionForDogFriendlyMessages } from "../reservation/description/description-for-dog-friendly-form-partial";
import type { DescriptionForDogRunMessages } from "../reservation/description/description-for-dog-run";
import { DescriptionForDogRun } from "../reservation/description/description-for-dog-run";
import DescriptionForEVChargerFormPartial, {
  evChargerOptionsFormSchema,
} from "../reservation/description/description-for-ev-charger-form-partial";
import type { DescriptionForEVChargerMessages } from "../reservation/description/description-for-ev-charger-form-partial";
import type { DescriptionForLargeAccommodationMessages } from "../reservation/description/description-for-large-accommodation";
import { DescriptionForLargeAccommodation } from "../reservation/description/description-for-large-accommodation";
import type { DescriptionForOnsenMessages } from "../reservation/description/description-for-onsen";
import { DescriptionForOnsen } from "../reservation/description/description-for-onsen";
import DescriptionForRemoteSiteCancelFeeFormPartial, {
  remoteSiteCancelFeeOptionsFormSchema,
} from "../reservation/description/description-for-remote-site-cancel-fee-form-partial";
import type { DescriptionForRemoteSiteCancelFeeMessages } from "../reservation/description/description-for-remote-site-cancel-fee-form-partial";
import type { DescriptionForSaunaMessages } from "../reservation/description/description-for-sauna";
import { DescriptionForSauna } from "../reservation/description/description-for-sauna";
import {
  DescriptionForSelection,
  type DescriptionForSelectionMessages,
} from "../reservation/description/description-for-selection";
import type { DescriptionForSmallAccommodationMessages } from "../reservation/description/description-for-small-accommodation";
import { DescriptionForSmallAccommodation } from "../reservation/description/description-for-small-accommodation";
import type { HouseRulesMessages } from "../reservation/house-rules/reservation-house-rules";
import { useHouseRules } from "../reservation/house-rules/use-house-rules";
import type { AccommodationAndPeriodMessages } from "../ui/accommodation-and-period/accommodation-and-period";
import { DraftAccommodationReservationInfo } from "../ui/accommodation-reservation-info/draft-accommodation-reservation-info";
import { InstructionList } from "../ui/instruction-list/instruction-list";
import { UserStripeCardInfo } from "../user-stripe-card-info";
import type { UserStripeCardInfoMessages } from "../user-stripe-card-info";

import {
  CancelFeePolicyArea,
  formatRateBasisJa,
} from "./cancel-fee-policy-area";
import type {
  CancelFeeOTAMessages,
  CancelFeeStayTicketMessages,
} from "./cancel-fee-policy-area";
import { useChangeReservationDialog } from "./dialogs/change-reservation-dialog";
import { ReservationInvitationDialog } from "./dialogs/invitation-dialog";
import { reservationErrorMessageOnCreate } from "./error-messages";
import { PriceBreakdown } from "./price-breakdown";
import type { PriceBreakdownMessages } from "./price-breakdown";
import { useAllowRoomChangeTerms } from "./special-terms/allow-room-change-terms";
import { useIchinomiyaSpecialTerms } from "./special-terms/ichinomiya-special-terms";
import type { Ichinomiya1stSpecialTermsMessages } from "./special-terms/ichinomiya1st-special-terms";
import type { Ichinomiya2ndSpecialTermsMessages } from "./special-terms/ichinomiya2nd-special-terms";
import type { Izu1stOnsenSuspensionTermsMessages } from "./special-terms/izu1st-onsen-suspension-terms";
import { useIzu1stOnsenSuspensionTerms } from "./special-terms/izu1st-onsen-suspension-terms";
import type { LocalTaxConsentTermsMessages } from "./special-terms/local-tax-consent-terms";
import { useLocalTaxConsentTerms } from "./special-terms/local-tax-consent-terms";
import type { Nasu3rdFrozenTermsMessages } from "./special-terms/nasu3rd-frozen-terms";
import { useNasu3rdFrozenTerms } from "./special-terms/nasu3rd-frozen-terms";
import { SubscriptionCancelFeePolicyArea } from "./subscription-cancel-fee-policy-area";

export const Query = graphql(`
  query DraftAccommodationReservation(
    $input: DraftAccommodationReservationInput!
    $provider: AccommodationReservationProviderInput!
    $lastNight: PlainDate!
  ) {
    draftAccommodationReservation(input: $input) {
      firstNight
      lastNight
      nights
      reservedFrom
      reservedTo
      standardCheckInTime
      firstDate
      lastDate
      accommodationNotices {
        brief
        detail
        externalUrl
      }
      accommodation {
        id
        name
        code
        facilityTypes
        site {
          slug
          isRemote
          name
          area {
            slug
          }
          checkInAuthenticationType
        }
        architectureType {
          series
          seriesShortName
          name
          size
          capacity
          additionalFee
        }
        isOwned(provider: $provider)
        accommodationClass(provider: $provider, night: $lastNight)
        classRelation(provider: $provider, night: $lastNight)
      }
      options {
        bringDog
        numberOfDogs
        useEvCharger
      }
      fee {
        totalAmount
        items {
          type
          key
          label
          amount
          night
        }
        billingMethod
      }
      feeApplicability {
        waiveArchitectureTypeAdditionalFee
        waiveDogFee
        waiveSaunaFee
        waiveOnsenFee
        waiveEvChargerFee
        waiveDogRunFee
      }
      additionalNights
      canSelectAllowRoomChange
      localTaxPolicies {
        id
      }
      stayTicketUnit {
        reservationInfo {
          type
          additionalGuestFeeStartsAt
          additionalGuestFeeAdultPricePerNight
          additionalGuestFeeKidsPricePerNight
        }
        ...StayTicketUnitForCancelFeePolicyFragment
      }
      cancellationPolicyContent {
        ...CancellationPolicyContentFragment
      }
      ...DraftAccommodationReservationInfo
    }
  }
`);

const Mutation = graphql(`
  mutation CreateAccommodationReservation(
    $input: CreateAccommodationReservationInput!
  ) {
    createAccommodationReservation(input: $input) {
      ... on AccommodationReservation {
        id
        invitationUrl
        tentativeDeadline
        userAcceptCode
      }
      ... on CreateAccommodationReservationError {
        case
        internalCode
      }
    }
  }
`);

const QueryForMaybeViewer = graphql(`
  query MaybeViewer {
    maybeViewer {
      stripe {
        defaultCard {
          last4
        }
      }
      fullOwnerships {
        plan {
          accommodation {
            id
          }
        }
      }
      subscription {
        id
      }
    }
  }
`);

export const OriginalAccommodationReservationFragment = graphql(`
  fragment OriginalAccommodationReservationOnDraftReservationForm on AccommodationReservation {
    id
    mutationScope
    canCancel
    isTentative
    detail {
      numberOfAdults
      numberOfKids
      numberOfInfants
      previousLocation
      nextLocation
      bringDog
      numberOfDogs
      useEvCharger
      allowRoomChange
    }
    fee {
      items {
        type
        label
        amount
        night
        key
      }
    }
  }
`);
// Note: fee, isTentative は InternalDraftReservationForm の方でのみ利用

export const reservationFormSchema = z
  .object({
    numberOfAdults: z
      .number({
        error: "人数を入力してください",
      })
      .min(1, "大人は１人以上必須です"),
    numberOfKids: z
      .number({
        error: "人数を入力してください",
      })
      .min(0, "人数を入力してください"),
    numberOfInfants: z
      .number({
        error: "人数を入力してください",
      })
      .min(0, "人数を入力してください"),
    previousLocation: z.string().min(1, "必須項目です"),
    nextLocation: z.string().min(1, "必須項目です"),
    dogFriendlyOptions: dogFriendlyOptionsSchema,
    evChargerOptions: evChargerOptionsFormSchema,
    remoteSiteCancelFeeOptions: remoteSiteCancelFeeOptionsFormSchema,
    // canSelectAllowRoomChange が false の場合は undefined になる
    allowRoomChange: z.boolean().optional(),
    // ユーザーが入力しないがバリデーションで使用
    // API で取得後にセットする
    capacity: z.number(),
    canSelectAllowRoomChange: z.boolean(),
  })
  .superRefine((data, ctx) => {
    // canSelectAllowRoomChange が true の場合、allowRoomChange の選択は必須
    if (data.canSelectAllowRoomChange && data.allowRoomChange === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "部屋変更の可否を選択してください",
        path: ["allowRoomChange"],
      });
    }
    // 大人と子供の合計人数が定員を超えていないかチェック
    if (data.numberOfAdults + data.numberOfKids > data.capacity) {
      // NOTE: なぜか分けて登録しないとエラーメッセージが表示されない...
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "大人と子供（12歳未満）の合計人数が定員を超えています",
        path: ["numberOfAdults"],
      });
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "大人と子供（12歳未満）の合計人数が定員を超えています",
        path: ["numberOfKids"],
      });
    }
  });

export type DraftReservationFormValues = z.infer<typeof reservationFormSchema>;

type DraftReservation = Omit<
  DocumentType<typeof Query>["draftAccommodationReservation"],
  "__typename"
>;

export type DraftAccommodationReservationOptions = {
  bringDog: boolean;
  numberOfDogs: number;
  useEvCharger: boolean;
};

export type DraftReservationFormMessages = {
  guestCount: string;
  capacityNote: (capacity: number) => string;
  // TS は引数の少ない実装(例: (startsAt) => string)もこの型に代入可能と判定する
  // （JS の「余分な引数は無視できる」規約により、呼び出し元が少ない引数しか使わなくてもコンパイルエラーにならない）。
  // 引数を増減させたら全 messages 実装（JA_MESSAGES とロケール別ページ側）を grep で確認すること。
  additionalGuestFeeNote: (
    startsAt: number,
    adultPrice: number,
    kidsPrice: number,
  ) => string;
  infantsExclusionNote: string;
  adults: string;
  kidsUpTo12: string;
  infantsUnder6: string;
  previousLocation: string;
  nextLocation: string;
  otaBillingNote1: string;
  otaBillingNote2: string;
  accommodationAndPeriod: AccommodationAndPeriodMessages;
  cancelFeeOTA: CancelFeeOTAMessages;
  cancelFeeStayTicket: CancelFeeStayTicketMessages;
  houseRules: HouseRulesMessages;
  fridaySundayFee: {
    intro: string;
    feeLabel: (formattedFee: string) => string;
    suffix: string;
    note: string;
  };
  priceBreakdown: PriceBreakdownMessages;
  descriptions?: {
    sauna?: DescriptionForSaunaMessages;
    onsen?: DescriptionForOnsenMessages;
    dogRun?: DescriptionForDogRunMessages;
    dogFriendly?: DescriptionForDogFriendlyMessages;
    evCharger?: DescriptionForEVChargerMessages;
    selection?: DescriptionForSelectionMessages;
    large?: DescriptionForLargeAccommodationMessages;
    small?: DescriptionForSmallAccommodationMessages;
    remoteCancelFee?: DescriptionForRemoteSiteCancelFeeMessages;
    accommodationClass?: DescriptionForAccommodationClassMessages;
  };
  specialTerms?: {
    nasu3rdFrozen?: Nasu3rdFrozenTermsMessages;
    izu1stOnsenSuspension?: Izu1stOnsenSuspensionTermsMessages;
    localTaxConsent?: LocalTaxConsentTermsMessages;
    ichinomiya1st?: Ichinomiya1stSpecialTermsMessages;
    ichinomiya2nd?: Ichinomiya2ndSpecialTermsMessages;
  };
  userStripeCardInfo?: UserStripeCardInfoMessages;
  unitCharacter: string;
};

const JA_MESSAGES: DraftReservationFormMessages = {
  guestCount: "ご利用人数",
  capacityNote: (capacity: number) => `最大${capacity}名まで`,
  additionalGuestFeeNote: (
    startsAt: number,
    adultPrice: number,
    kidsPrice: number,
  ) =>
    // 円表記に固定。messages.unitCharacter（¥表記もありうる）とは独立した表記のため連動させない
    `${startsAt}名以上は大人1人あたり${formatCurrency(adultPrice, { unitCharacter: "円" })}/泊が追加されます\n  （6-11歳の子供は${formatCurrency(kidsPrice, { unitCharacter: "円" })}/泊）`,
  infantsExclusionNote: "※6歳未満のお子さんは対象人数に含まれません",
  adults: "大人",
  kidsUpTo12: "小学生まで(12歳未満)",
  infantsUnder6: "6歳未満",
  previousLocation: "前泊地",
  nextLocation: "行先地",
  otaBillingNote1: "宿泊予約サイトで決済される料金は含まれません",
  otaBillingNote2: "施設内の有料備品をご利用された際には料金が発生します。",
  accommodationAndPeriod: {
    roomNumber: "部屋番号",
    facility: "施設",
    options: "オプション",
    checkIn: "チェックイン",
    checkOut: "チェックアウト",
    nightsAndDays: (nights: number, days: number) => `${nights}泊${days}日`,
    dayOfWeek: (day: number) =>
      ["", "月", "火", "水", "木", "金", "土", "日"][day] ?? "",
    formatFacilityTypes: (types) => getAccommodationFacilitiesLabel(types),
  },
  cancelFeeOTA: {
    title: "キャンセル料金のルールについて",
    intro:
      "キャンセルや予約変更により宿泊をとりやめた場合、宿泊日ごとに以下の料金が発生します。",
    rule7days: "7日前15時以降：宿泊料金の60%",
    rule3days: "3日前15時以降：宿泊料金の100%",
    noShow:
      "宿泊日の翌日11時までにチェックインがない場合、ノーショー（No Show）料金として、1泊あたり宿泊料金の100%が請求されます。",
  },
  cancelFeeStayTicket: {
    title: "キャンセル料金のルールについて",
    intro:
      "キャンセルや予約変更により宿泊をとりやめた場合、宿泊日ごとに以下の料金が発生します。",
    introImmediate:
      "キャンセルにより宿泊をとりやめた場合、宿泊日ごとに以下の料金が発生します。",
    cannotChangeReservation:
      "チェックイン日の前日15時以降は、チェックイン日と宿泊施設を変更できません。",
    cannotChangeReservationImmediate: "予約内容の変更はできません。",
    ruleByRate: ({ daysBeforeCheckin, rate, rateBasis }) => {
      const period =
        daysBeforeCheckin === 1
          ? "前日15時以降"
          : `${daysBeforeCheckin}日前15時以降`;
      return `${period}：${formatRateBasisJa(rateBasis)}の${rate}%`;
    },
    ruleByFee: ({ daysBeforeCheckin, fee }) => {
      const period =
        daysBeforeCheckin === 1
          ? "前日15時以降"
          : `${daysBeforeCheckin}日前15時以降`;
      return `${period}：${fee.toLocaleString("ja-JP")}円`;
    },
    ruleImmediate: () => "予約確定後：宿泊料金の100%",
    noShowByRate: ({ rate, rateBasis }) =>
      `宿泊日の翌日11時までにチェックインがない場合、ノーショー（No Show）料金として、1泊あたり${formatRateBasisJa(rateBasis)}の${rate}%が請求されます。`,
    noShowImmediate: () =>
      "宿泊日の翌日11時までにチェックインがない場合、ノーショー（No Show）料金として、1泊あたり宿泊料金の100%が請求されます。",
    noShowByFee: ({ fee }) =>
      `宿泊日の翌日11時までにチェックインがない場合、ノーショー（No Show）料金として、1泊あたり${fee.toLocaleString("ja-JP")}円が請求されます。`,
  },
  houseRules: {
    titleAlt: "ハウスルール",
    titleText: "ハウスルール",
    description:
      "SANU 2nd Homeはホテルではなく、みんなでシェアする自然の中の別荘です。互いにルールを守りながら、大切に使っていただけると嬉しいです。",
    dishLine1: "お皿は洗って拭き、",
    dishLine2: "もとの場所へ",
    quiet22Line1: "22時以降は室内でお静かに",
    quiet21Line1: "21時以降は室内でお静かに",
    quietLine2: "お過ごしください",
    fireLine1: "火気は必ず焚き火台スペースで",
    fireLine2: "（焚き火台のない拠点では、火気の使用はご遠慮ください）",
    trashLine1: "ゴミは分別し、チェックアウト",
    trashLine2: "前にゴミステーションへ",
    note: "※詳しくは館内の案内をご確認ください。",
    agreeButton: "ハウスルールに同意します",
  },
  fridaySundayFee: {
    intro: "選択された日程には、",
    feeLabel: (formattedFee) =>
      `金曜・日曜の追加料金（1泊あたり${formattedFee}）`,
    suffix: "がかかります。",
    note: "（追加料金は、カレンダーに表示されている料金にあらかじめ反映されています）",
  },
  priceBreakdown: {
    dynamicPricingLabel: "お部屋・日程による変動料金",
    additionalGuestFeeLabel: "人数追加料金を含む",
    total: "合計",
    includingTax: "(税込)",
  },
  unitCharacter: "円",
};

const styles = {
  separator: "border-b border-b-skogecha100 last:hidden my-[20px]",
};

/**
 * 予約内容（期間、設備や各種条件、およびそれに基づく料金）を表示した上で、予約作成・予約変更を行うフォーム
 *
 * @params provider 予約のプロバイダー
 * @params reservation 作成・変更したい予約内容
 * @params updateOptions オプションが更新された時のコールバック関数
 * @params originalReservationData 予約変更の場合の変更前の予約データ（更新時に渡す）
 * @params formRef フォームの参照
 * @params submitArea 予約確定ボタンの代わりに表示するエリア
 * @params onSubmit submit された時のコールバック関数
 * @params onCanSubmitChange submit 可能かどうかのフラグが変化した時のコールバック関数
 * @params cancelFeePolicyHidden キャンセル料ポリシーを非表示にするかどうか
 * @params termsAgreementNoticeHidden 予約確定ボタン押下で約款に同意したとみなす旨の注記を非表示にするかどうか
 * @params additionalNotice 追加の注意事項
 * @params forceShowingCreditCardForm クレジットカード情報を常に表示するかどうか
 *         背景: https://github.com/sa-nu/2h/pull/2302#discussion_r2080802152
 * @params showHouseRules ゲスト向けハウスルールを表示するかどうか
 * @params isRoomTypeReservation 部屋タイプ予約として扱うか。部屋は既存通り割り当てるが
 *         「チェックイン2日前まで部屋番号変更あり」の制約とし、部屋変更可否ヒアリングを省略して
 *         常に許可扱いにする（注記を表示）。true のとき作成 mutation の input.isRoomTypeReservation に渡り、
 *         サーバ側で allowRoomChange=true 強制＋フラグ永続化される。
 */
export default function DraftReservationForm({
  provider,
  reservation,
  updateOptions,
  originalReservationData,
  formRef,
  submitArea,
  onSubmit,
  onCanSubmitChange,
  cancelFeePolicyHidden = false,
  termsAgreementNoticeHidden = false,
  additionalNotice,
  forceShowingCreditCardForm = false,
  showHouseRules = false,
  loading = false,
  messages = JA_MESSAGES,
  isRoomTypeReservation = false,
}: {
  provider: AccommodationReservationProvider;
  reservation: DraftReservation;
  updateOptions: (options: DraftAccommodationReservationOptions) => void;
  originalReservationData: FragmentType<
    typeof OriginalAccommodationReservationFragment
  > | null;
  formRef?: React.RefObject<HTMLFormElement | null>;
  submitArea?: ReactNode;
  onSubmit?: (data: DraftReservationFormValues) => void;
  onCanSubmitChange?: (canSubmit: boolean) => void;
  cancelFeePolicyHidden?: boolean;
  termsAgreementNoticeHidden?: boolean;
  additionalNotice?: ReactNode;
  forceShowingCreditCardForm?: boolean;
  showHouseRules?: boolean;
  loading?: boolean;
  messages?: DraftReservationFormMessages;
  isRoomTypeReservation?: boolean;
}) {
  const { user } = useUser();
  const gtm = useGtm();

  const isTicketProvider =
    provider.__typename === "StayTicketAccommodationReservationProvider";

  const defaultFormRef = useRef<HTMLFormElement>(null);
  const { data: maybeViewerData, loading: loadingMaybeViewer } =
    useQuery(QueryForMaybeViewer);
  const originalReservation = useFragment(
    OriginalAccommodationReservationFragment,
    originalReservationData,
  );

  const accommodation = reservation.accommodation;
  const additionalGuestFeeStartsAt =
    reservation.stayTicketUnit?.reservationInfo.additionalGuestFeeStartsAt ??
    null;
  const additionalGuestFeeAdultPricePerNight =
    reservation.stayTicketUnit?.reservationInfo
      .additionalGuestFeeAdultPricePerNight ?? null;
  const additionalGuestFeeKidsPricePerNight =
    reservation.stayTicketUnit?.reservationInfo
      .additionalGuestFeeKidsPricePerNight ?? null;
  const {
    appliesFrom,
    isStraddling,
    isArchitectureTypeAdditionalFeeDeprecated,
  } = useRuleRevision2027ForStay({ provider, reservation });
  // 適用開始日の注記は、新旧ルールの料金が混在する跨ぎ滞在だけに出す
  const ruleRevision2027AppliesFrom = isStraddling ? appliesFrom : null;
  const classAdditionalFeePerNight =
    reservation.fee.items.find(
      (item) =>
        item.key === ("calculated_class_additional_fee" satisfies PriceKey) &&
        item.amount > 0,
    )?.amount ?? null;
  const facilityFeeWaiver = resolveFacilityFeeWaiver(accommodation);

  const showSelection =
    accommodation.architectureType.series === "Selection" &&
    !isArchitectureTypeAdditionalFeeDeprecated;
  const accommodationClass = accommodation.accommodationClass ?? null;
  const showAccommodationClass =
    classAdditionalFeePerNight !== null && accommodationClass !== null;
  const showDogFriendly = accommodation.facilityTypes.includes(
    AccommodationFacilityType.DogFriendly,
  );
  const showEVCharger = accommodation.facilityTypes.includes(
    AccommodationFacilityType.EvCharger,
  );
  const showSauna = accommodation.facilityTypes.includes(
    AccommodationFacilityType.Sauna,
  );
  const showOnsen = accommodation.facilityTypes.includes(
    AccommodationFacilityType.Onsen,
  );
  const showDogRun = accommodation.facilityTypes.includes(
    AccommodationFacilityType.DogRun,
  );

  const showLargeAccommodation =
    accommodation.architectureType.size === "Large" &&
    !isArchitectureTypeAdditionalFeeDeprecated;
  const showSmallAccommodation =
    accommodation.architectureType.size === "Small";
  const showRemoteCancelFee = shoudShowRemoteCancelPolicy(
    accommodation,
    reservation,
  );

  const isWeekdayPlan =
    provider.__typename === "StandardPlanAccommodationReservationProvider";

  // 料金表示用の変数
  const groupedFeeItems = groupFeeItemByNight(reservation.fee.items);
  const hasDynamicPricing = groupedFeeItems.some(
    (item) => item.hasDynamicPricing,
  );
  const hasFridaySundayFee = groupedFeeItems.some(
    (item) => item.hasFridaySundayFee,
  );
  const fridaySundayFeeAmount = reservation.fee.items.find(
    (item) => item.key === "friday_sunday_original",
  )?.amount;

  const [createAccommodationReservation, { loading: mutationLoading }] =
    useMutation(Mutation);

  // FormProviderから提供されたフォームメソッドを使用
  const methods = useFormContext<DraftReservationFormValues>();

  const {
    register,
    control,
    formState,
    handleSubmit,
    getValues,
    setError,
    setValue,
  } = methods;

  // 最初から大人人数のエラーを表示
  // ただしハイライトだけでメッセージは表示させない
  useEffect(() => {
    if (!getValues("numberOfAdults")) {
      setError("numberOfAdults", {
        type: "required",
        message: " ",
      });
    }
  }, [getValues, setError]);

  // DraftReservation を更新するために
  // オプション変化時に親コンポーネントに通知
  const bringDog = useWatch({
    name: "dogFriendlyOptions.bringDog",
    control,
  });
  const numberOfDogs = useWatch({
    name: "dogFriendlyOptions.numberOfDogs",
    control,
  });
  const useEvCharger = useWatch({
    name: "evChargerOptions.useEvCharger",
    control,
  });
  const allowRoomChange = useWatch({
    name: "allowRoomChange",
    control,
  });
  useEffect(() => {
    if (
      bringDog !== reservation.options.bringDog ||
      numberOfDogs !== reservation.options.numberOfDogs ||
      useEvCharger !== reservation.options.useEvCharger
    ) {
      updateOptions({
        bringDog: bringDog,
        numberOfDogs: bringDog ? numberOfDogs : 0,
        useEvCharger: useEvCharger,
      });
    }
  }, [
    bringDog,
    numberOfDogs,
    useEvCharger,
    updateOptions,
    reservation.options.bringDog,
    reservation.options.numberOfDogs,
    reservation.options.useEvCharger,
  ]);

  const {
    renderIchinomiyaSpecialTerms,
    agreedOrUnnecessary: agreedWithIchinomiyaSpecialTermsOrUnnecessary,
  } = useIchinomiyaSpecialTerms(accommodation.site.slug, {
    ichinomiya1st: messages.specialTerms?.ichinomiya1st,
    ichinomiya2nd: messages.specialTerms?.ichinomiya2nd,
  });
  const {
    renderNasu3rdFrozenTerms,
    agreedOrUnnecessary: agreedWithNasu3rdFrozenTermsOrUnnecessary,
  } = useNasu3rdFrozenTerms(
    accommodation.site.slug,
    reservation.firstNight,
    messages.specialTerms?.nasu3rdFrozen,
  );
  const {
    renderIzu1stOnsenSuspensionTerms,
    agreedOrUnnecessary: agreedWithIzu1stOnsenSuspensionTermsOrUnnecessary,
  } = useIzu1stOnsenSuspensionTerms(
    accommodation.site.slug,
    accommodation.architectureType.seriesShortName,
    messages.specialTerms?.izu1stOnsenSuspension,
  );
  const { renderHouseRules, agreed: agreedWithHouseRules } = useHouseRules(
    accommodation.site.area.slug,
    accommodation.architectureType.series,
    messages.houseRules,
  );

  const {
    renderLocalTaxConsentTerms,
    agreedOrUnnecessary: agreedWithLocalTaxOrUnnecessary,
  } = useLocalTaxConsentTerms(
    reservation.localTaxPolicies.length > 0,
    messages.specialTerms?.localTaxConsent,
  );

  // クレジットカード情報（バリデーション用）
  const [creditCardInfo, setCreditCardInfo] = useState<StripeCard | null>(null);

  const isLogin =
    maybeViewerData?.maybeViewer !== null &&
    maybeViewerData?.maybeViewer !== undefined;
  const defaultCard = maybeViewerData?.maybeViewer?.stripe.defaultCard ?? null;

  const canSubmit =
    formState.isValid &&
    agreedWithIchinomiyaSpecialTermsOrUnnecessary &&
    agreedWithNasu3rdFrozenTermsOrUnnecessary &&
    agreedWithIzu1stOnsenSuspensionTermsOrUnnecessary &&
    (!showHouseRules || (showHouseRules && agreedWithHouseRules)) &&
    agreedWithLocalTaxOrUnnecessary &&
    (!isLogin ||
      reservation.fee.billingMethod === "Manual" ||
      defaultCard !== null ||
      creditCardInfo !== null);

  // ロード後にクレジットカード情報がない場合に、クレジットカード登録フォームを表示。
  // 一度表示したら、カードを登録しても表示は消さないようにする。
  const [showCreditCardForm, setShowCreditCardForm] = useState(false);
  useEffect(() => {
    if (
      !showCreditCardForm &&
      !loadingMaybeViewer &&
      isLogin &&
      defaultCard === null
    ) {
      setShowCreditCardForm(true);
    }
  }, [defaultCard, isLogin, loadingMaybeViewer, showCreditCardForm]);

  useEffect(() => {
    onCanSubmitChange?.(canSubmit);
  }, [canSubmit, onCanSubmitChange]);

  const onlyExit = originalReservation?.mutationScope === "OnlyExit";

  const { renderAllowRoomChangeTerms } = useAllowRoomChangeTerms({
    canSelectAllowRoomChange: reservation.canSelectAllowRoomChange,
    allowRoomChange,
    setValue,
    disabled: onlyExit,
  });

  const router = useRouter();
  const refetchReservations = useRefetchReservations();
  const {
    renderDialog: renderChangeReservationDialog,
    openDialog: openChangeReservationDialog,
  } = useChangeReservationDialog();

  const showAlertDialog = useAlertDialog();
  const showSnackbar = useSnackbar();

  const isInNativeApp = useInNativeApp();
  const accommodationTermsLink = useAccommodationTermsLink(provider);

  const [showInvitationDialog, setShowInvitationDialog] = useState(false);
  const [invitationDialogParams, setInvitationDialogParams] = useState<{
    invitationUrl: string;
    tentativeDeadline: ZonedDateTime;
  } | null>(null);
  const [showAskToSendUrlDialog, setShowAskToSendUrlDialog] = useState(false);

  const onSubmitReservation = async (data: DraftReservationFormValues) => {
    if (!reservation) return;
    const {
      dogFriendlyOptions: { bringDog, numberOfDogs },
      evChargerOptions: { useEvCharger },
      nextLocation,
      previousLocation,
      numberOfAdults,
      numberOfKids,
      numberOfInfants,
      allowRoomChange,
    } = data;
    const detail: AccommodationReservationDetailInput = {
      bringDog,
      numberOfDogs: bringDog ? numberOfDogs : 0,
      useEvCharger,
      nextLocation,
      previousLocation,
      numberOfAdults,
      numberOfInfants,
      numberOfKids,
      // canSelectAllowRoomChange が false の場合は undefined でサーバーに任せる（新規予約のみ）
      // 予約変更の場合は後で元の値を引き継ぐ処理がある（665-667行目）
      ...(reservation.canSelectAllowRoomChange ? { allowRoomChange } : {}),
    };

    if (!originalReservation) {
      // 予約作成
      createAccommodationReservation({
        variables: {
          input: {
            accommodationId: reservation.accommodation.id,
            firstNight: reservation.firstNight,
            lastNight: reservation.lastNight,
            provider: buildAccommodationReservationProviderInput(provider),
            detail,
            isRoomTypeReservation: isRoomTypeReservation,
          },
        },
      }).then(({ data }) => {
        const body = data!.createAccommodationReservation;
        switch (body.__typename) {
          case "AccommodationReservation":
            switch (provider.__typename) {
              case "OneMoreCabinAccommodationReservationProvider": {
                // 予約招待 URL 発行ダイアログを表示
                const { tentativeDeadline, invitationUrl } = body;
                if (invitationUrl && tentativeDeadline) {
                  setInvitationDialogParams({
                    tentativeDeadline: ZonedDateTime.from(tentativeDeadline),
                    invitationUrl,
                  });
                  setShowInvitationDialog(true);
                }
                if (isInNativeApp) {
                  // アプリに通知する
                  postMessageToNativeApp(
                    {
                      type: "OneMoreCabinCreate",
                      payload: {
                        reservationId: body.id,
                      },
                    },
                    window,
                  );
                }
                break;
              }
              case "StayTicketAccommodationReservationProvider": {
                if (user) {
                  // ログイン済みならばプロフィールページに遷移
                  router.push(`/tentative-reservations/${body.id}/profile`);
                } else {
                  // 未ログインの時はサインアップ経由で accept に遷移
                  const userAcceptCode = body.userAcceptCode;
                  if (userAcceptCode) {
                    router.push(
                      `/auth/signup?returnTo=${encodeURIComponent(`/tentative-reservations/accept?code=${userAcceptCode}`)}`,
                    );
                  } else {
                    // 起きえないはずだけど一応
                    showAlertDialog({
                      title: "予約エラー",
                      mediumBody: "予約時にエラーが発生しました。",
                    });
                  }
                }
                break;
              }
              default:
                if (isInNativeApp) {
                  // アプリに通知する
                  postMessageToNativeApp(
                    {
                      type: "ReservationCreate",
                      payload: {
                        reservationId: body.id,
                      },
                    },
                    window,
                  );
                } else {
                  router.push(`/reservations`);
                  showSnackbar({ message: "予約が確定しました" });
                }
                // 予約完了イベントを送信
                gtm.sendReservationCompletedEvent({
                  provider,
                  platform: isInNativeApp ? "app" : "web",
                });
                break;
            }
            break;
          case "CreateAccommodationReservationError": {
            switch (body.case) {
              case "OwnerOfStayGiftFromOwnersError":
                setShowAskToSendUrlDialog(true);
                break;
              default: {
                const [mediumBody, normalBody] =
                  reservationErrorMessageOnCreate(body.case);
                showAlertDialog({
                  title: "予約エラー",
                  mediumBody,
                  normalBody,
                });
                if (isInNativeApp) {
                  const messageType =
                    provider.__typename ===
                    "OneMoreCabinAccommodationReservationProvider"
                      ? "OneMoreCabinCreate"
                      : "ReservationCreate";
                  postMessageToNativeApp(
                    {
                      type: messageType,
                      payload: {
                        case: "GeneralError",
                      },
                    },
                    window,
                  );
                }
              }
            }
          }
        }
      });
    } else {
      // 予約変更
      openChangeReservationDialog({
        reservationId: originalReservation.id,
        draftReservation: {
          provider,
          detail: {
            ...detail,
            // canSelectAllowRoomChangeがfalseの場合、元の予約のallowRoomChangeを引き継ぐ
            allowRoomChange: reservation.canSelectAllowRoomChange
              ? detail.allowRoomChange
              : originalReservation.detail.allowRoomChange,
            numberOfTotalChildren: data.numberOfKids + data.numberOfInfants,
            internalStamp: null,
          },
          firstNight: reservation.firstNight,
          lastNight: reservation.lastNight,
          reservedFrom: reservation.reservedFrom,
          reservedTo: reservation.reservedTo,
          standardCheckInTime: reservation.standardCheckInTime,
          accommodation: reservation.accommodation,
          fee: reservation.fee,
        },
      });
    }
  };

  const feeApplicability = reservation.feeApplicability;

  const isOTAProvider =
    provider.__typename === "OnlineTravelAgentAccommodationReservationProvider";

  // ご利用人数・了承事項。部屋タイプ予約では予約内容テーブルの下にカードとして置くため、配置先を切り替えられるよう変数に切り出す。
  const guestCountAndTermsFields = (
    <>
      <div className="flex flex-col gap-[2px]">
        <span className="text-sm">{messages.guestCount}</span>
        {additionalGuestFeeStartsAt != null &&
          additionalGuestFeeAdultPricePerNight != null &&
          additionalGuestFeeKidsPricePerNight != null &&
          accommodation.architectureType.capacity >=
            additionalGuestFeeStartsAt && (
            <span className="my-[16px] text-xs whitespace-pre-line">
              {messages.additionalGuestFeeNote(
                additionalGuestFeeStartsAt,
                additionalGuestFeeAdultPricePerNight,
                additionalGuestFeeKidsPricePerNight,
              )}
            </span>
          )}
        <span className="text-xs">
          {messages.capacityNote(accommodation.architectureType.capacity)}
        </span>
        <span className="text-skogecha-80 text-xs">
          {messages.infantsExclusionNote}
        </span>
      </div>
      <div className="mt-[20px] flex w-full flex-col gap-[24px]">
        <div className="flex w-full flex-col gap-[15px] md:flex-row md:gap-[15px]">
          <div
            className={inputTextStyle(
              "w-full",
              formState.errors.numberOfAdults?.message ||
                formState.errors.numberOfKids?.message ||
                formState.errors.numberOfInfants?.message,
            )}
          >
            <div className="font-roboto text-skogecha-80 mb-[4px] text-[10px] leading-relaxed wrap-break-word">
              {messages.adults}
            </div>
            <input
              type="number"
              {...register("numberOfAdults", {
                valueAsNumber: true,
              })}
              readOnly={onlyExit}
              placeholder={messages.adults}
              min="1"
              data-testid="number-of-adults-input"
              className="h-[32px] w-full font-normal outline-hidden read-only:text-[#bebebf]"
            />
            <ValidationError
              message={
                formState.errors.numberOfAdults?.message ??
                formState.errors.numberOfKids?.message ??
                formState.errors.numberOfInfants?.message
              }
            />
          </div>
          <div
            className={inputTextStyle(
              "w-full",
              formState.errors.numberOfKids?.message,
            )}
          >
            <div className="font-roboto text-skogecha-80 mb-[4px] text-[10px] leading-relaxed wrap-break-word">
              {messages.kidsUpTo12}
            </div>
            <input
              type="number"
              {...register("numberOfKids", {
                valueAsNumber: true,
              })}
              readOnly={onlyExit}
              placeholder={messages.kidsUpTo12}
              min="0"
              className="h-[32px] w-full font-normal outline-hidden read-only:text-[#bebebf]"
            />
          </div>
          <div
            className={inputTextStyle(
              "w-full",
              formState.errors.numberOfInfants?.message,
            )}
          >
            <div className="font-roboto text-skogecha-80 mb-[4px] text-[10px] leading-relaxed wrap-break-word">
              {messages.infantsUnder6}
            </div>
            <input
              type="number"
              {...register("numberOfInfants", {
                valueAsNumber: true,
              })}
              readOnly={onlyExit}
              placeholder={messages.infantsUnder6}
              min="0"
              className="h-[32px] w-full font-normal outline-hidden read-only:text-[#bebebf]"
            />
          </div>
        </div>
        <div className="flex w-full flex-col gap-[15px] md:flex-row md:justify-start md:gap-[15px]">
          <div
            className={inputTextStyle(
              "w-full",
              formState.errors.previousLocation?.message,
            )}
          >
            <div className="font-roboto text-skogecha-80 mb-[4px] text-[10px] leading-relaxed wrap-break-word">
              {messages.previousLocation}
            </div>
            <input
              type="text"
              {...register("previousLocation")}
              readOnly={onlyExit}
              placeholder={messages.previousLocation}
              data-testid="previous-location-input"
              className="h-[32px] w-full font-normal outline-hidden read-only:text-[#bebebf]"
            />
            <ValidationError
              message={formState.errors.previousLocation?.message}
            />
          </div>
          <div
            className={inputTextStyle(
              "w-full",
              formState.errors.nextLocation?.message,
            )}
          >
            <div className="font-roboto text-skogecha-80 mb-[4px] text-[10px] leading-relaxed wrap-break-word">
              {messages.nextLocation}
            </div>
            <input
              type="text"
              {...register("nextLocation")}
              placeholder={messages.nextLocation}
              data-testid="next-location-input"
              className="h-[32px] w-full font-normal outline-hidden"
            />
            <ValidationError message={formState.errors.nextLocation?.message} />
          </div>
        </div>
      </div>
      {/* Facilities */}
      <div className="mt-[30px]">
        {showSelection && (
          <>
            <DescriptionForSelection
              additionalFee={
                feeApplicability.waiveArchitectureTypeAdditionalFee
                  ? 0
                  : accommodation.architectureType.additionalFee
              }
              hasDynamicPricing={hasDynamicPricing || isWeekdayPlan}
              hideFixedFee={isWeekdayPlan}
              ruleRevision2027AppliesFrom={ruleRevision2027AppliesFrom}
              messages={messages.descriptions?.selection}
            />
            <hr className={styles.separator} />
          </>
        )}
        {showLargeAccommodation && (
          <>
            <DescriptionForLargeAccommodation
              additionalFee={
                feeApplicability.waiveArchitectureTypeAdditionalFee
                  ? 0
                  : priceAmounts.large_accommodation_original
              }
              hasDynamicPricing={hasDynamicPricing}
              ruleRevision2027AppliesFrom={ruleRevision2027AppliesFrom}
              messages={messages.descriptions?.large}
            />
            <hr className={styles.separator} />
          </>
        )}
        {showSmallAccommodation && (
          <>
            <DescriptionForSmallAccommodation
              messages={messages.descriptions?.small}
            />
            <hr className={styles.separator} />
          </>
        )}
        {showAccommodationClass && (
          <>
            <DescriptionForAccommodationClass
              accommodationClass={accommodationClass}
              additionalFeePerNight={classAdditionalFeePerNight}
              ruleRevision2027AppliesFrom={ruleRevision2027AppliesFrom}
              messages={messages.descriptions?.accommodationClass}
            />
            <hr className={styles.separator} />
          </>
        )}
        {showSauna && (
          <>
            <DescriptionForSauna
              additionalFee={
                feeApplicability.waiveSaunaFee
                  ? 0
                  : priceAmounts.sauna_use_original
              }
              hasDynamicPricing={hasDynamicPricing}
              feeWaiver={facilityFeeWaiver}
              messages={messages.descriptions?.sauna}
            />
            <hr className={styles.separator} />
          </>
        )}
        {showOnsen && (
          <>
            <DescriptionForOnsen
              additionalFee={
                feeApplicability.waiveOnsenFee
                  ? 0
                  : priceAmounts.onsen_use_original
              }
              hasDynamicPricing={hasDynamicPricing}
              feeWaiver={facilityFeeWaiver}
              messages={messages.descriptions?.onsen}
            />
            <hr className={styles.separator} />
          </>
        )}
        {showDogRun && (
          <>
            <DescriptionForDogRun
              additionalFee={
                feeApplicability.waiveDogRunFee
                  ? 0
                  : priceAmounts.dog_run_use_original
              }
              hasDynamicPricing={hasDynamicPricing}
              feeWaiver={facilityFeeWaiver}
              messages={messages.descriptions?.dogRun}
            />
            <hr className={styles.separator} />
          </>
        )}
        {showDogFriendly && (
          <>
            <Controller
              name="dogFriendlyOptions"
              control={control}
              render={({ field }) => (
                <DescriptionForDogFriendlyFormPartial
                  {...field}
                  additionalFee={
                    feeApplicability.waiveDogFee
                      ? 0
                      : priceAmounts.bring_dog_original
                  }
                  feeWaiver={facilityFeeWaiver}
                  messages={messages.descriptions?.dogFriendly}
                />
              )}
            />
            <hr className={styles.separator} />
          </>
        )}
        {showEVCharger && (
          <>
            <Controller
              name="evChargerOptions"
              control={control}
              render={({ field }) => (
                <DescriptionForEVChargerFormPartial
                  {...field}
                  additionalFee={
                    feeApplicability.waiveEvChargerFee
                      ? 0
                      : priceAmounts.ev_charger_use_original
                  }
                  messages={messages.descriptions?.evCharger}
                />
              )}
            />
            <hr className={styles.separator} />
          </>
        )}
        {showRemoteCancelFee && (
          <>
            <DescriptionForRemoteSiteCancelFeeFormPartial
              messages={messages.descriptions?.remoteCancelFee}
            />
            <hr className={styles.separator} />
          </>
        )}
        {hasFridaySundayFee && (
          <>
            <div className="mt-[20px] w-full space-y-[20px]">
              <div className="text-xs md:text-sm">
                {messages.fridaySundayFee.intro}
                <span className="font-bold">
                  {messages.fridaySundayFee.feeLabel(
                    formatCurrency(
                      fridaySundayFeeAmount ??
                        priceAmounts.friday_sunday_original,
                      {
                        unitCharacter: toUnitCharacter(messages.unitCharacter),
                      },
                    ),
                  )}
                </span>
                {messages.fridaySundayFee.suffix}
                <br />
                {messages.fridaySundayFee.note}
              </div>
            </div>
            <hr className={styles.separator} />
          </>
        )}
      </div>
      {!isRoomTypeReservation && renderAllowRoomChangeTerms()}
      {renderNasu3rdFrozenTerms()}
      {renderIzu1stOnsenSuspensionTerms()}
      {renderLocalTaxConsentTerms()}
    </>
  );

  return (
    <>
      <fieldset
        disabled={loading}
        className={clsx(
          "w-full",
          loading ? "pointer-events-none opacity-50" : "",
        )}
      >
        <form
          onSubmit={handleSubmit(onSubmit ?? onSubmitReservation)}
          ref={formRef ?? defaultFormRef}
          className={clsx(
            "pt-global-header-min md:pt-global-header-md flex w-full flex-col items-center",
            isRoomTypeReservation ? "bg-white" : "bg-slightbeige",
          )}
        >
          {isRoomTypeReservation ? (
            <div className="w-full bg-white pt-[60px]">
              <div className="font-roboto flex w-full flex-col items-center">
                <div className="text-skogecha flex w-full flex-col gap-[32px] px-[30px] md:w-[690px] md:px-0">
                  <DraftAccommodationReservationInfo
                    provider={provider}
                    draftReservation={reservation}
                    messages={messages.accommodationAndPeriod}
                    isRoomTypeReservation={isRoomTypeReservation}
                  />
                  <div
                    className="bg-slightbeige-100 w-full p-[24px] font-medium"
                    data-testid="guest-count-and-terms-card"
                  >
                    {guestCountAndTermsFields}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="text-skogecha flex w-full flex-col items-center">
                <div className="w-full px-[30px] pt-[10px] pb-[30px] font-medium md:w-[690px] md:px-0">
                  {guestCountAndTermsFields}
                </div>
              </div>
              <div className="w-full bg-white pt-[60px]">
                <div className="font-roboto flex w-full flex-col items-center">
                  <div className="text-skogecha w-full px-[30px] md:w-[690px] md:px-0">
                    <DraftAccommodationReservationInfo
                      provider={provider}
                      draftReservation={reservation}
                      messages={messages.accommodationAndPeriod}
                      isRoomTypeReservation={isRoomTypeReservation}
                    />
                  </div>
                </div>
              </div>
            </>
          )}
          <div
            className={
              isTicketProvider ? "w-full bg-white" : "w-full bg-white pb-[60px]"
            }
          >
            {/* 料金確認＆送信ボタン */}
            <div className="font-roboto flex w-full flex-col items-center">
              <div
                className={clsx(
                  "text-skogecha w-full px-[30px] md:w-[690px] md:px-0",
                  // 白エリアを上下に分けたため、上側の最後の要素との gap-[32px] を margin で補う
                  isRoomTypeReservation && "mt-[32px] flex flex-col gap-[32px]",
                )}
              >
                {isRoomTypeReservation &&
                  reservation.accommodationNotices.length > 0 && (
                    <ReservationNoticesSection
                      notices={reservation.accommodationNotices}
                    />
                  )}
                <div className={clsx(!isRoomTypeReservation && "mt-[20px]")}>
                  <PriceBreakdown
                    groupedFeeItems={groupedFeeItems}
                    totalAmount={reservation.fee.totalAmount}
                    messages={{
                      ...messages.priceBreakdown,
                      unitCharacter: messages.unitCharacter,
                    }}
                  />
                  {reservation.fee?.billingMethod === "Manual" && (
                    <p className="mt-4 text-sm">
                      お支払い方法：後日請求（登録カードへの自動決済は行われません）
                    </p>
                  )}
                  {isOTAProvider && (
                    <InstructionList
                      instructions={[
                        messages.otaBillingNote1,
                        messages.otaBillingNote2,
                      ]}
                      mark="※"
                      className="text-[14px] font-bold"
                      lineSpacing="narrow"
                    />
                  )}
                </div>
              </div>
              {reservation.additionalNights > 0 && (
                <AdditionalCycleNightsFee
                  nights={reservation.additionalNights}
                />
              )}
              {!cancelFeePolicyHidden &&
                (reservation.stayTicketUnit || isOTAProvider) && (
                  <div
                    className={clsx(
                      "w-full px-[30px] md:w-[690px] md:px-0",
                      isRoomTypeReservation ? "mt-[32px]" : "mt-[16px]",
                    )}
                  >
                    <CancelFeePolicyArea
                      stayTicketUnit={reservation.stayTicketUnit}
                      otaMessages={messages.cancelFeeOTA}
                      stayTicketMessages={messages.cancelFeeStayTicket}
                    />
                  </div>
                )}
              {!cancelFeePolicyHidden &&
                provider.__typename ===
                  "StandardPlanAccommodationReservationProvider" && (
                  <div className="w-full px-[30px] md:w-[690px] md:px-0">
                    <SubscriptionCancelFeePolicyArea
                      cancellationPolicy={
                        reservation.cancellationPolicyContent ?? undefined
                      }
                    />
                  </div>
                )}
              {additionalNotice && (
                <div className="w-full px-[30px] md:w-[690px] md:px-0">
                  {additionalNotice}
                </div>
              )}
            </div>

            {renderIchinomiyaSpecialTerms()}
            {showHouseRules && renderHouseRules()}

            {/**
             * ユーザーがクレジットカード情報を持っていない場合のみカード入力フォームを表示させる。
             * ログインしていない場合には表示させない。
             */}
            {(showCreditCardForm || forceShowingCreditCardForm) &&
              reservation.fee.billingMethod !== "Manual" && (
                <section className="bg-slightbeige mt-[30px] w-full py-[30px]">
                  <div className="mx-auto w-full bg-white md:w-[690px]">
                    <UserStripeCardInfo
                      onCardChange={setCreditCardInfo}
                      messages={messages.userStripeCardInfo}
                    />
                  </div>
                </section>
              )}
            {submitArea || (
              <div className="font-roboto flex w-full flex-col items-center bg-white">
                <div className="text-skogecha w-full px-[30px] md:w-[690px] md:px-0">
                  {!termsAgreementNoticeHidden && (
                    <div className="mt-[30px] text-xs font-medium md:text-center md:text-sm">
                      予約確定ボタンを押した時点で、
                      {accommodationTermsLink ? (
                        <a
                          data-analytics-id="reservation_form_clicked_accommodation_terms"
                          href={accommodationTermsLink.url}
                          target="_blank"
                          className="underline"
                        >
                          {accommodationTermsLink.title}
                        </a>
                      ) : (
                        // 基本的にはリンクが取得できるはずなので、ここには来ないはず。
                        "宿泊約款"
                      )}
                      に同意したとみなされます。
                    </div>
                  )}
                  <div className="mt-[20px] flex w-full flex-col items-center justify-center">
                    <button
                      data-analytics-id="reservation_form_submitted_confirm"
                      type="submit"
                      className={
                        isTicketProvider
                          ? "bg-dx-button-dark-fill font-notojp text-dx-base-white flex h-[52px] w-full items-center justify-center rounded-full text-[16px] leading-[1.6] font-bold shadow-[0px_6px_14px_rgba(0,0,0,0.1),0px_0px_0px_rgba(0,0,0,0.02)] disabled:opacity-60"
                          : "btn-primary w-full"
                      }
                      disabled={!canSubmit || mutationLoading}
                      data-testid="submit-reservation-button"
                      onClick={
                        !originalReservation
                          ? () => gtm.sendEvent("rsv_create_submit_clicked")
                          : undefined
                      }
                    >
                      {originalReservation
                        ? "予約を変更する"
                        : isTicketProvider
                          ? "ログイン / 新規登録して予約する"
                          : "予約を確定する"}
                    </button>
                    {!canSubmit && (
                      <div className="text-sorange mt-2 text-[12px] font-bold">
                        必要項目を全て入力してください
                      </div>
                    )}
                  </div>
                  {renderChangeReservationDialog({
                    onComplete: () => {
                      refetchReservations();
                      router.push("/reservations");
                    },
                  })}
                  {originalReservation?.canCancel && (
                    <div className="mt-[20px] flex w-full justify-center">
                      <CancelReservationDialog
                        reservationId={originalReservation.id}
                        onComplete={() => {
                          refetchReservations();
                          router.push("/member");
                        }}
                      >
                        <button
                          data-analytics-id="reservation_form_opened_cancel"
                          className="btn-secondary w-full"
                        >
                          予約をキャンセルする
                        </button>
                      </CancelReservationDialog>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </form>
      </fieldset>
      <AskToSendUrlDialog
        isOpen={showAskToSendUrlDialog}
        onOpenChange={(open) => {
          setShowAskToSendUrlDialog(open);
        }}
        invitationUrl={window.location.href}
      />
      {invitationDialogParams && (
        <ReservationInvitationDialog
          open={showInvitationDialog}
          onOpenChange={(open) => {
            if (!open) {
              router.push("/reservations");
            }
            setShowInvitationDialog(open);
          }}
          {...invitationDialogParams}
        />
      )}
    </>
  );
  function AdditionalCycleNightsFee({ nights }: { nights: number }) {
    return (
      <div className="text-skogecha mb-[16px] w-full px-[30px] pt-[30px] md:w-[690px] md:px-0">
        <div className="text-2xl">8泊目以上の宿泊料金</div>
        <div className="mt-[20px]">
          <div className="flex flex-col justify-between border-t border-[rgba(37,39,42,0.1)] py-[12px] md:h-[60px] md:flex-row md:items-center">
            <div>本予約による8泊目以上の追加泊数</div>
            <div className="font-light">{reservation.additionalNights}泊</div>
          </div>
          <div className="flex flex-col justify-between border-t border-[rgba(37,39,42,1.0)] py-[12px] md:h-[60px] md:flex-row md:items-center">
            <div>
              本予約による8泊目以上の追加料金（
              {formatCurrency(
                priceAmounts.additional_after_eighth_night_original,
              )}
              /泊）
            </div>
            <div className="text-xl font-light">
              {formatCurrency(
                nights * priceAmounts.additional_after_eighth_night_original,
              )}
              <span className="text-xs">(税込)</span>
            </div>
          </div>
        </div>
        <div className="font-roboto mt-[4px] text-[12px] font-medium">
          8泊目以上の追加料金は、実際の滞在泊数に応じて各チェックアウト時に宿泊料金として請求されます。
        </div>
      </div>
    );
  }
}

function inputTextStyle(custom: string, isError: unknown) {
  return clsx(
    "min-h-[56px] rounded-t bg-white px-[12px] pb-[12px] pt-[10px] flex flex-col",
    custom,
    {
      "border-b border-b-skogecha": !isError,
      "border-b-2 border-b-sorange": isError,
    },
  );
}

function ValidationError({ message }: { message: string | undefined }) {
  if (!message) return null;
  return (
    <div className="relative top-[10px] left-[-11px] overflow-visible text-xs whitespace-nowrap text-red-500">
      {message}
    </div>
  );
}
